import type { EmailIntegrationAccountRow } from '@elva/ui';
import type { EmailIntegration } from '../api/types';

function listFromWire(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];

  const obj = data as Record<string, unknown>;
  for (const key of ['accounts', 'mailboxes', 'results', 'data', 'items']) {
    if (Array.isArray(obj[key])) return obj[key] as unknown[];
  }
  return [];
}

function readAccountEmail(row: Record<string, unknown>): string {
  for (const key of ['email_address', 'email', 'address', 'from_email']) {
    if (typeof row[key] === 'string' && row[key].trim()) return row[key].trim();
  }
  return '';
}

function readAccountId(row: Record<string, unknown>): string | null {
  for (const key of ['id', 'account_id', 'mailbox_id']) {
    const raw = row[key];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  }
  return null;
}

function normalizeIntegrationAccountRow(row: unknown): EmailIntegrationAccountRow | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  const emailAddress = readAccountEmail(r);
  if (!emailAddress) return null;

  const id = readAccountId(r) ?? emailAddress;

  return {
    id,
    emailAddress,
    provider: typeof r.provider === 'string' ? r.provider : 'gmail',
    status: typeof r.status === 'string' ? r.status : 'active',
    lastSyncAt:
      typeof r.last_sync_at === 'string'
        ? r.last_sync_at
        : typeof r.lastSyncAt === 'string'
          ? r.lastSyncAt
          : null,
  };
}

/** Map `GET /api/settings/email-integration` wire payloads to UI rows. */
export function adaptEmailIntegrationAccounts(
  data: EmailIntegration | undefined,
): EmailIntegrationAccountRow[] {
  if (!data) return [];

  const rows = listFromWire(data)
    .map(normalizeIntegrationAccountRow)
    .filter((row): row is EmailIntegrationAccountRow => row != null);
  if (rows.length > 0) return rows;

  if (typeof data !== 'object') return [];

  const root = data as Record<string, unknown>;
  const emailAddress = readAccountEmail(root);
  if (!emailAddress) return [];

  const connected =
    root.connected === true ||
    root.is_connected === true ||
    ['active', 'connected', 'syncing', 'pending'].includes(
      String(root.status ?? '').trim().toLowerCase(),
    );

  if (!connected && root.connected !== undefined) return [];

  return [
    {
      id: readAccountId(root) ?? emailAddress,
      emailAddress,
      provider: typeof root.provider === 'string' ? root.provider : 'gmail',
      status: typeof root.status === 'string' ? root.status : 'active',
      lastSyncAt:
        typeof root.last_sync_at === 'string'
          ? root.last_sync_at
          : typeof root.lastSyncAt === 'string'
            ? root.lastSyncAt
            : null,
    },
  ];
}
